import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeaderAccount from "../../../components/HeaderAccount";
import { useTranslation } from "react-i18next";
import { SquareLoader } from "react-spinners";
import axiosWithAuth from "../../../utils/axiosWithAuth";
import { getApiEndpoint } from "../../../utils/apiConfig";
import "./_HomePlan.scss";

const HomePlan = () => {
  const { t } = useTranslation();
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  document.title = t("homePlan.0");
  const headerTitle = t("homePlan.0");
  const backwardNavigate = "/account";
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserCalculations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const api = axiosWithAuth();
        
        // Fetch both repair calculations and breakdowns
        const [calculationsResponse, breakdownsResponse] = await Promise.all([
          api.get(getApiEndpoint('repairs/calculations')).catch(() => ({ data: { data: [] } })),
          api.get(getApiEndpoint('repairs/breakdowns')).catch(() => ({ data: { data: [] } }))
        ]);

        console.log("Calculations response:", calculationsResponse.data);
        console.log("Breakdowns response:", breakdownsResponse.data);
        
        let allCalculations = [];
        
        // Add repair calculations
        if (calculationsResponse.data && calculationsResponse.data.success && Array.isArray(calculationsResponse.data.data)) {
          allCalculations = [...calculationsResponse.data.data];
        }
        
        // Add breakdowns (these are from DirectSave)
        if (breakdownsResponse.data && breakdownsResponse.data.success && Array.isArray(breakdownsResponse.data.data)) {
          const breakdownCalculations = breakdownsResponse.data.data.map(breakdown => ({
            _id: breakdown._id,
            repairStyle: breakdown.repairStyle,
            repairTariff: breakdown.repairTariff,
            costs: breakdown.costs,
            breakdown: {
              rooms: breakdown.rooms || [],
              fixtures: breakdown.fixtures || {}
            },
            roomCount: breakdown.roomsCount || breakdown.rooms?.length || 0,
            createdAt: breakdown.createdAt,
            property: breakdown.property,
            isFromBreakdown: true // Flag to identify source
          }));
          
          allCalculations = [...allCalculations, ...breakdownCalculations];
        }
        
        // Filter valid calculations
        const validCalculations = allCalculations.filter(calc => 
          calc && calc._id
        );
        
        // Sort by creation date (newest first)
        validCalculations.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        console.log("All valid calculations:", validCalculations);
        setCalculations(validCalculations);
      } catch (error) {
        console.error("Error fetching calculations:", error);
        setError("Failed to load properties. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserCalculations();
  }, []);

  const handlePropertyClick = (calculationId) => {
    if (!calculationId) {
      console.error("Invalid calculation ID");
      return;
    }
    navigate(`/allmaterials/${calculationId}?breakdown=true`);
  };

  // Helper function to extract property info from calculation
  const getPropertyInfo = (calculation) => {
    // Try different property sources
    const property = calculation.property || 
                    calculation.breakdown?.property || 
                    calculation.propertyData;
    
    // Helper function to extract text from multilingual objects
    const extractText = (obj) => {
      if (!obj) return null;
      if (typeof obj === 'string') return obj;
      if (typeof obj === 'object') {
        return obj.text || obj.az || obj.en || obj.ru || null;
      }
      return null;
    };
    
    if (property) {
      const propertyTypeName = extractText(property.propertyType?.name) || 
                              extractText(property.propertyType) ||
                              t("homePlan.3");
      
      return {
        id: property.id || property._id,
        type: propertyTypeName,
        icon: property.propertyType?.iconType || "fa-solid fa-building",
        area: property.area || property.totalArea || 0,
        roomCount: property.roomCount || calculation.roomCount || 0
      };
    }
    
    // Fallback: create property info from calculation data
    const repairStyleName = extractText(calculation.repairStyle?.name) || 
                           extractText(calculation.repairStyle) ||
                           t("homePlan.3");
    
    return {
      id: calculation._id,
      type: repairStyleName,
      icon: "fa-solid fa-home",
      area: calculation.breakdown?.rooms?.reduce((total, room) => total + (room.area || 0), 0) || 0,
      roomCount: calculation.roomCount || calculation.breakdown?.rooms?.length || 0
    };
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <SquareLoader
          color="#cda742"
          cssOverride={{
            position: "absolute",
            top: "45%",
            bottom: "45%",
          }}
          size={100}
          speedMultiplier={1}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-home-plan-section mx-3">
        <HeaderAccount title={headerTitle} backwardNavigate={backwardNavigate} />
        <div className="alert alert-danger mt-3">{error}</div>
      </div>
    );
  }

  if (!calculations || calculations.length === 0) {
    return (
      <div className="account-home-plan-section mx-3">
        <HeaderAccount title={headerTitle} backwardNavigate={backwardNavigate} />
        <div className="middle-section">
          <div className="empty-state">
            <div className="empty-icon">
              <i className="fas fa-home"></i>
            </div>
            <h5>{t("homePlan.2")}</h5>
            <p>Hesablamalarınızı görmək üçün yeni bir təmir hesablaması yaradın</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-home-plan-section mx-3">
      <HeaderAccount title={headerTitle} backwardNavigate={backwardNavigate} />
      <div className="middle-section">
        <p className="text-muted mb-3">{t("homePlan.1")}</p>
        <div className="row g-3">
          {calculations.map((calculation, index) => {
            const propertyInfo = getPropertyInfo(calculation);
            
            return (
              <div className="col-12" key={calculation._id || index}>
                <div
                  className="property-card"
                  onClick={() => handlePropertyClick(calculation._id)}
                >
                  <div className="card-header">
                    <div className="property-icon">
                      <i className={propertyInfo.icon}></i>
                    </div>
                    <div className="calculation-badge">
                      {t("general.calculation")}
                    </div>
                  </div>
                  
                  <div className="card-content">
                    <div className="property-title">
                      <h5>{propertyInfo.type}</h5>
                    </div>
                    
                    <div className="property-stats">
                      <div className="stat-item">
                        <div className="stat-icon">
                          <i className="fas fa-door-open"></i>
                        </div>
                        <div className="stat-text">
                          {propertyInfo.roomCount} {t("general.room")}
                        </div>
                      </div>
                      
                      <div className="stat-item">
                        <div className="stat-icon">
                          <i className="fas fa-ruler-combined"></i>
                        </div>
                        <div className="stat-text">
                          {propertyInfo.area.toFixed(0)}m²
                        </div>
                      </div>
                      
                      <div className="stat-item">
                        <div className="stat-icon">
                          <i className="fas fa-coins"></i>
                        </div>
                        <div className="stat-text">
                          {calculation.costs?.generalCost?.toFixed(0) || '0'} AZN
                        </div>
                      </div>
                      
                      <div className="stat-item">
                        <div className="stat-icon">
                          <i className="fas fa-calendar"></i>
                        </div>
                        <div className="stat-text">
                          {calculation.costs?.duration || 1} gün
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-footer">
                    <div className="creation-date">
                      <i className="fas fa-clock"></i>
                      {new Date(calculation.createdAt).toLocaleDateString('az-AZ')}
                    </div>
                    <div className="view-details">
                      {t("general.viewDetails")}
                      <i className="fas fa-arrow-right"></i>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HomePlan;
